<template>
    <Card :title="translate('labels.products')">
        <template #header-elements>
            <Dropdown
                :title="translate('buttons.actions')"
                :icon="'icon-menu7'"
                :disabled="selectedUuids.length === 0"
                class="mr-2"
            >
                <template #menu>
                    <a @click="destroy" class="dropdown-item">
                        <i class="icon-bin2"></i>
                        {{ translate('buttons.delete') }}
                    </a>
                </template>
            </Dropdown>
        </template>
        <DataTable
            :url="table.url"
            :columns="table.columns"
            :orders-props="[{ column: 0, dir: 'desc'}]"
            ref="DataTable"
            :check-box-props="true"
            @changeSelectedIds="newSelectedIds => selectedUuids = newSelectedIds"
            identifier="uuid"
        >
            <template #rowForCreate>
                <td></td>
                <td></td>
                <td></td>
                <td>
                    <Autocomplete
                        @update:model-value="newProduct => selectProductItem(newProduct.product)"
                        :model-value="productItem.product_item_id"
                        :selected-value="productItem.label"
                        option-key="product.uuid"
                        option-value="product.name"
                        url="/management/warehouses/products/availability"
                        ref="Autocomplete"
                        :disabled="disabled"
                        :query-params="{
                            extra: 1,
                            only_with_relation: 0,
                            orders: [
                                {column: 'automatic_arrival', dir: 'desc'},
                                {column: 'lack', dir: 'asc'}
                            ],
                            warehouse_uuids: [selectedWarehouse?.uuid]}"
                        :selected-values="$refs.DataTable ? $refs.DataTable.meta.product_uuids : []"
                    >
                        <template #optionItem="{option}">
                            {{ option.product.name }}
                            <ProductAvailabilitySelectOption :option="option"/>
                        </template>
                    </Autocomplete>
                </td>
                <td>
                    <Input
                        ref="ProductItemAmount"
                        @keydown:enter="focusNextInput()"
                        v-model="productItem.amount"
                        :disabled="productItem.product_item_id === null || disabled"
                        type="number"
                    />
                </td>
                <td>
                    <Input
                        @keydown:enter="focusNextInput()"
                        v-model="productItem.cost"
                        :disabled="productItem.product_item_id === null || disabled"
                        type="number"
                    />
                </td>
                <td></td>
                <td style="text-align: center;">
                    <Button size="sm"
                        class="float-left"
                        color="teal"
                        :disabled="disabled"
                        @click="focusNextInput()"
                    >
                        {{ translate('buttons.create') }}
                    </Button>
                </td>
            </template>
            <template v-slot:row-main_image_url="{ row }">
                <ImageZoomComponent
                    :url="row.main_image_url !== null ? row.main_image_url : PLACEHOLDER_IMAGE_URL"
                    :alt="row.title"
                    :default-width="50"
                    :default-height="50"
                />
            </template>
            <template v-slot:row-sku="{row}">
                <router-link v-if="row?.product_item_uuid" :to="{ name: 'EditProductPage', params: { productUuid: row.product_item_uuid } }" style="white-space: nowrap;">{{ truncateTitle(row.title) }}</router-link>
                <span v-else style="white-space: nowrap;">{{ truncateTitle(row.title) }}</span><br/>
                <span class="text-muted">{{ row.sku }}</span>
            </template>
            <template #row-amount="{row}">
                {{ Amount.factory(row.amount).toMeasurementString(row.measurement.name) }}
                <Popover @hidePopover="hideFields" @showPopover="showFields(row)" v-if="!disabled">
                    <template #body>
                        <label>{{ translate('table.titles.amount') }}</label>
                        <Input
                            type="number"
                            v-model="basic[0].value"
                        />
                        <Button size="sm"
                            @click="edit(row.uuid, 'amount')"
                            color="teal"
                        >
                            {{ translate('buttons.edit') }}
                        </Button>
                    </template>
                </Popover>
            </template>
            <template #row-cost="{row}">
                {{ Price.factory(row.cost).toCurrencyString(getCurrencyCodeByUuid(currencyUuid)) }}
                <slot v-if="currencyUuid !== mainCurrency.uuid && mainCurrency.uuid !== null">
                    <br/><span class="text-muted">{{ Price.factory(row.cost).convertByRate(currencyRate).toCurrencyString(mainCurrency.code) }}</span>
                </slot>
                <Popover @hidePopover="hideFields" @showPopover="showFields(row)">
                    <template #body>
                        <label>{{ translate('table.titles.cost') }}</label>
                        <Input
                            type="number"
                            v-model="basic[1].value"
                        />
                        <Button size="sm"
                            @click="edit(row.uuid, 'cost')"
                            color="teal"
                        >
                            {{ translate('buttons.edit') }}
                        </Button>
                    </template>
                </Popover>
            </template>
            <template #row-sum="{row}">
                {{ Price.factory(row.sum).toCurrencyString(getCurrencyCodeByUuid(currencyUuid)) }}
                <slot v-if="currencyUuid !== mainCurrency.uuid && mainCurrency.uuid !== null">
                    <br/><span class="text-muted">{{ Price.factory(row.sum).convertByRate(currencyRate).toCurrencyString(mainCurrency.code) }}</span>
                </slot>
            </template>
            <template #row-created_at="{row}">
                {{ DateFormatter.formatDateTime(row.created_at) }}
            </template>
        </DataTable>
    </Card>
</template>

<script>
import Card from '../../../../../components/new/CardComponent.vue'
import DataTable from '../../../../../components/table/DataTableComponent.vue'
import Button from '../../../../../components/new/button/AppButton.vue'
import Input from '../../../../../components/new/form/Input.vue'
import Popover from '../../../../../components/PopoverComponent.vue'
import Autocomplete from '../../../../../components/new/form/autocomplete/AppAutoComplete.vue'
import Dropdown from '../../../../../components/DropdownComponent.vue'
import Amount from '../../../../../services/Amount'
import Price from '../../../../../services/Price/Price'
import ProductAvailabilitySelectOption from '../../../../../components/new/form/select/ProductAvailabilitySelectOption.vue'
import { httpClient } from '../../../../../services/HttpClient'
import { PLACEHOLDER_IMAGE_URL } from '../../../../../constants'
import { chunk } from '../../../../../utils'
import DateFormatter from '../../../../../services/DateFormatter'
import MessageNotificator from '../../../../../services/MessageNotificator.js'
import ImageZoomComponent from '../../../../../components/new/image/ImageZoomComponent.vue'
export default {
    name: 'ProductItemTable',
    props: {
        arrivalDeclaration: {
            type: Object,
            default: null
        },
        currencyUuid: {
            type: String,
            required: true
        },
        currencyRate: {
            type: [Number, String],
            required: true
        },
        mainCurrency: {
            type: Object,
            default: () => ({uuid: null})
        },
        currencies: {
            type: Array,
            required: true
        },
        selectedWarehouse: {
            type: Object,
            default: null
        }
    },
    components: {
        Card,
        DataTable,
        Button,
        Input,
        Popover,
        Autocomplete,
        Dropdown,
        ProductAvailabilitySelectOption,
        ImageZoomComponent
    },
    data() {
        return {
            DateFormatter,
            PLACEHOLDER_IMAGE_URL,
            Price: Price,
            Amount: Amount,
            table: {
                url: `/management/arrival-declarations/${this.$route.params.arrivalDeclarationUuid}/arrival-declaration-product-items/table`,
                columns: [
                    {
                        data: "id",
                        name: "id",
                        searchable: 1,
                        orderable: true,
                        field: translate('table.titles.id')
                    },
                    {
                        width: "50px",
                        data: "main_image_url",
                        name: "main_image_url",
                        searchable: 0,
                        orderable: false,
                        field: translate('table.titles.image')
                    },
                    {
                        data: "sku",
                        name: "sku",
                        searchable: 1,
                        width: '460px',
                        orderable: true,
                        field: translate('table.titles.name')
                    },
                    {
                        data: "amount",
                        name: "amount",
                        searchable: 0,
                        orderable: true,
                        field: translate('table.titles.amount')
                    },
                    {
                        data: "cost",
                        name: "cost",
                        searchable: 0,
                        orderable: true,
                        field: translate('table.titles.cost')
                    },
                    {
                        data: "sum",
                        name: "sum",
                        searchable: 0,
                        orderable: true,
                        field: translate('table.titles.sum')
                    },
                    {
                        data: "created_at",
                        name: "created_at",
                        searchable: 0,
                        orderable: true,
                        field: translate('table.titles.created_at')
                    },
                ]
            },
            basic: [
                {
                    name: 'amount',
                    value: null
                },
                {
                    name: 'cost',
                    value: null
                },
            ],
            productItem: {
                product_item_id: null,
                label: '',
                cost: null,
                amount: null
            },
            selectedUuids: []
        }
    },
    methods: {
        truncateTitle(title) {
            if (title.length <= 80) {
                return title
            }

            const truncated = title.slice(0, 80)

            return `${truncated}...`
        },
        destroy() {
            if(!this.disabled) {
                new MessageNotificator().confirm(`${translate('alerts.confirm_for_delete', {model: translate('labels.product_item')})}`, async () => {
                    let request = this.selectedUuids.map(uuid => {
                        return {
                            method: 'DELETE',
                            path: `/management/arrival-declarations/product-items/${uuid}`
                        }
                    })

                    await Promise.all(chunk(request, 10).map(request => httpClient.post('/batch', {
                        data: request
                    })))

                    this.selectedIds = []
                    this.$refs.DataTable.load()
                })
            }
        },
        edit(productItemUuid, property) {
            let fields = []
            switch(property) {
                case 'amount':
                    fields = [this.basic[0]]
                    break
                case 'cost':
                    fields = [this.basic[1]]
                    break
            }
            httpClient.put(`/management/arrival-declarations/${this.arrivalDeclaration.uuid}/product-items/${productItemUuid}`, {
                fields: fields
            }).then(() => {
                new MessageNotificator().success(translate('alerts.success'))
                this.$root.$emit('bv::hide::popover')
                this.$refs.DataTable.load()
            })
        },
        showFields(row) {
            this.basic = [
                {
                    name: 'amount',
                    value: row.amount
                },
                {
                    name: 'cost',
                    value: row.cost
                },
            ]
        },
        hideFields() {
            this.basic = [
                {
                    name: 'amount',
                    value: null
                },
                {
                    name: 'cost',
                    value: null
                },
            ]
        },
        selectProductItem(productItem) {
            new Promise((resolve) => {
                this.productItem.product_item_id = productItem.id;
                this.productItem.label = productItem.name;
                resolve()
            }).then(() => {
                this.$refs.ProductItemAmount.focus()
            })
        },
        focusNextInput() {
            let request = {}
                Object.entries(this.productItem).forEach(([key, value]) => {
                    if(key !== 'label' && value !== null && value !== "" ) {
                        request[key] = value
                    }
                })
            httpClient.post(`/management/arrival-declarations/${this.arrivalDeclaration.uuid}/product-items`, request, ).then(() => {
                new MessageNotificator().success(translate('alerts.added', { model: translate('labels.product_item') }));
                this.$refs.DataTable.load();
                this.productItem.product_item_id = null;
                this.productItem.label = '';
                this.productItem.amount = null;
                this.productItem.cost = null;
            })
        },
        getCurrencyCodeByUuid(currencyUuid) {
            if (!currencyUuid) return
            return this.currencies.find(currency => currency.uuid === currencyUuid)?.code ?? ''
        },
    },
    computed: {
        disabled() {
            if (!this.arrivalDeclaration) {
                return true
            }

            let statusType = this.arrivalDeclaration.arrival_declaration_status.type

            return statusType === 'done' || statusType === 'fail' 
        },
    },
}
</script>
<style scoped>
#input {
    margin-bottom: 10px;
}
#button {
    float: right; margin-bottom: 10px;
}
.dropdown-menu {
    right: 0px;
    left:auto;
    top: 105%
}
</style>
